import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { signInGuestWithSession } from "@/lib/guest";
import { Loader2, ShieldAlert, Anchor } from "lucide-react";
import { useBranding } from "@/lib/branding";

/**
 * `/visit/:token` — public claim page for Day Visitors and Floating Guests.
 *
 * A crew member mints an access token via `POST /admin/guests/access-token`,
 * hands out the QR (or copies the URL). The guest scans → this page →
 * `/public/guest-claim/{token}` → drops the credentials into localStorage
 * (same shape the PIN-based login uses) → auto-navigate to `/experience`.
 *
 * The token is HMAC-signed by the backend and has a TTL (defaults to end-of-day
 * for day visitors). Expired / tampered tokens land on a friendly error state
 * asking the guest to see the crew for a fresh QR.
 */
export default function GuestVisit() {
  const { token } = useParams();
  const nav = useNavigate();
  const { logoUrl, spaName } = useBranding();
  const [state, setState] = useState({ loading: true, error: null, guestName: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/guest-claim/${encodeURIComponent(token)}`);
        if (cancelled) return;
        // Drop into localStorage using the same shape the PIN login uses so
        // every downstream page (GuestMenu, GuestBooking, …) works unmodified.
        signInGuestWithSession(data);
        setState({ loading: false, error: null, guestName: data?.guest_name || "Guest" });
        // Small pause so the guest sees the welcome flash, then off to the menu.
        setTimeout(() => nav("/experience", { replace: true }), 750);
      } catch (e) {
        if (cancelled) return;
        setState({
          loading: false,
          error: formatErr(e?.response?.data?.detail) || "This access link is invalid or has expired.",
          guestName: null,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token, nav]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="guest-visit-page">
      <div className="max-w-md w-full text-center space-y-6">
        {logoUrl ? (
          <img src={logoUrl} alt={spaName} className="h-16 w-auto mx-auto opacity-80" />
        ) : (
          <div className="h-16 w-16 mx-auto rounded-full border border-border flex items-center justify-center">
            <Anchor className="h-8 w-8 text-primary/60" strokeWidth={1.4} />
          </div>
        )}
        {state.loading && (
          <>
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-accent" strokeWidth={1.6} />
            <div className="overline text-primary/60 text-xs">Checking your access…</div>
          </>
        )}
        {!state.loading && !state.error && (
          <div data-testid="guest-visit-welcome">
            <h1 className="font-display text-4xl">Welcome{state.guestName ? `, ${state.guestName}` : ""}</h1>
            <p className="text-sm text-muted-foreground mt-3">Opening the spa menu…</p>
          </div>
        )}
        {!state.loading && state.error && (
          <div data-testid="guest-visit-error" className="border border-destructive/40 bg-destructive/5 p-6 space-y-3">
            <ShieldAlert className="h-8 w-8 mx-auto text-destructive/80" strokeWidth={1.6} />
            <h2 className="font-display text-2xl">Access link expired</h2>
            <p className="text-sm text-muted-foreground">{state.error}</p>
            <p className="text-xs text-muted-foreground pt-2">Please ask the crew for a fresh QR code.</p>
          </div>
        )}
      </div>
    </div>
  );
}
